/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { Button as MUIButton, CircularProgress, IconButton } from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplayIcon from '@mui/icons-material/Replay';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ReduxState } from 'store';
import { ImperativeModalHandler, Modal } from 'components/Modal/Modal';
import { Button } from 'pages/FieldNotebook/components/Button/Button';
import cancelIcon from 'assets/icons/cancel.png';
import { prepareFileForUpload } from 'utils/shared/image';
import {
  directUploadFile,
  inferContentType,
  parseDirectUploadError,
  uploadErrorKey,
  validateUploadFile,
} from 'services/directUploadService';
import { TrackingService } from 'services/trackingService';
import {
  parseImageAnswer,
  serializeImageAnswer,
} from 'utils/fieldNotebookUtils/imageAnswer';
import {
  removeUploadPreview,
  resolveFilePreview,
  setUploadPreview,
} from 'utils/fieldNotebookUtils/uploadPreviewCache';
import {
  Buttons,
  Container,
  ErrorText,
  FilesGrid,
  HelperText,
  Label,
  RemoveIcon,
  Tile,
  TileBadge,
  TileFileName,
  TileOverlay,
} from './styles';

export const MAX_FILES_PER_FIELD = 10;
export const UPLOAD_CONCURRENCY = 3;

type ItemStatus = 'uploading' | 'done' | 'error';
type ItemKind = 'image' | 'pdf' | 'unknown';

interface UploadItem {
  id: string;
  status: ItemStatus;
  progress: number;
  /** signed_id (novo), URL do storage (existente) ou data URL (legado). */
  value?: string;
  previewUrl?: string;
  kind: ItemKind;
  name: string;
  /** Arquivo original, guardado para reenvio. */
  file?: File;
  errorKey?: string;
  abort?: AbortController;
}

interface UploadFileProps {
  setValue: (value: string) => void;
  defaultValue: string;
  /** Avisa quando há envio em andamento (bloqueia o avanço do formulário). */
  onBusyChange?: (busy: boolean) => void;
}

let sequence = 0;
const newId = () => {
  sequence += 1;
  return `upload-${sequence}`;
};

function itemsFromValue(value: string): UploadItem[] {
  return parseImageAnswer(value).map((item) => {
    const preview = resolveFilePreview(item);
    return {
      id: newId(),
      status: 'done',
      progress: 100,
      value: item,
      previewUrl: preview.src,
      kind: preview.kind,
      name: preview.name,
    };
  });
}

function doneValues(items: UploadItem[]): string[] {
  return items
    .filter((item) => item.status === 'done' && item.value)
    .map((item) => item.value as string);
}

function normalizeValue(value: string): string {
  return serializeImageAnswer(parseImageAnswer(value));
}

export default function UploadFile({
  setValue,
  defaultValue,
  onBusyChange,
}: UploadFileProps) {
  const { t } = useTranslation();
  const jwt = useSelector<ReduxState, string>((state) => state.user.jwt);

  const [items, setItems] = useState<UploadItem[]>(() => itemsFromValue(defaultValue));
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<UploadItem | null>(null);

  const lastValueRef = useRef(normalizeValue(defaultValue));
  const itemsRef = useRef(items);
  const mountedRef = useRef(true);
  const modalRef = useRef<ImperativeModalHandler>(null);

  itemsRef.current = items;

  // Valor vindo do formulário (ex.: voltar para um passo já respondido).
  useEffect(() => {
    const normalized = normalizeValue(defaultValue);
    if (normalized !== lastValueRef.current) {
      lastValueRef.current = normalized;
      setItems(itemsFromValue(defaultValue));
    }
  }, [defaultValue]);

  // Propaga para o formulário apenas os arquivos já enviados, na ordem escolhida.
  useEffect(() => {
    const serialized = serializeImageAnswer(doneValues(items));
    if (serialized !== lastValueRef.current) {
      lastValueRef.current = serialized;
      setValue(serialized);
    }
    onBusyChange?.(items.some((item) => item.status === 'uploading'));
  }, [items, setValue, onBusyChange]);

  useEffect(
    () => () => {
      mountedRef.current = false;
      itemsRef.current.forEach((item) => {
        item.abort?.abort();
        // Itens concluídos ficam no cache de pré-visualização (usado no resumo);
        // os demais nunca entraram no cache e precisam liberar o object URL aqui.
        if (item.status !== 'done' && item.previewUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
      onBusyChange?.(false);
    },
    [onBusyChange],
  );

  const updateItem = useCallback((id: string, patch: Partial<UploadItem>) => {
    if (!mountedRef.current) return;
    setItems((current) =>
      current.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    );
  }, []);

  const runUpload = useCallback(
    async (item: UploadItem) => {
      const { file } = item;
      if (!file) return;

      const abort = new AbortController();
      updateItem(item.id, { status: 'uploading', progress: 0, errorKey: undefined, abort });

      try {
        const prepared = await prepareFileForUpload(file, (progress) =>
          updateItem(item.id, { progress: Math.round(progress * 0.2) }),
        );
        if (abort.signal.aborted) return;

        const result = await directUploadFile(prepared, {
          jwt,
          signal: abort.signal,
          onProgress: (progress) =>
            updateItem(item.id, { progress: 20 + Math.round(progress * 0.8) }),
        });

        const kind: ItemKind = prepared.type === 'application/pdf' ? 'pdf' : 'image';
        const previewUrl =
          item.previewUrl ?? (kind === 'image' ? URL.createObjectURL(prepared) : undefined);

        if (previewUrl) {
          setUploadPreview(result.signedId, { previewUrl, kind, name: prepared.name });
        }

        TrackingService.sendEvent('fieldnotebook_file_upload_success', {
          content_type: result.contentType,
          byte_size: result.byteSize,
        });

        updateItem(item.id, {
          status: 'done',
          progress: 100,
          value: result.signedId,
          previewUrl,
          kind,
          name: prepared.name,
          abort: undefined,
        });
      } catch (error) {
        if (abort.signal.aborted) return;

        console.error('[direct-upload] falha ao enviar arquivo', error);
        const uploadError = parseDirectUploadError(error);
        TrackingService.sendEvent('fieldnotebook_file_upload_error', {
          step: uploadError.step,
          status: uploadError.status,
        });

        updateItem(item.id, {
          status: 'error',
          errorKey: uploadErrorKey(error),
          abort: undefined,
        });
      }
    },
    [jwt, updateItem],
  );

  const uploadInBatches = useCallback(
    async (queue: UploadItem[]) => {
      for (let index = 0; index < queue.length; index += UPLOAD_CONCURRENCY) {
        // Poucos envios simultâneos: em conexão ruim, paralelismo demais só gera timeout.
        // eslint-disable-next-line no-await-in-loop
        await Promise.all(queue.slice(index, index + UPLOAD_CONCURRENCY).map(runUpload));
      }
    },
    [runUpload],
  );

  const handleFilesSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    // Permite escolher o mesmo arquivo de novo depois de removê-lo.
    event.target.value = '';
    if (files.length === 0) return;

    setErrorKey(null);

    const available = MAX_FILES_PER_FIELD - itemsRef.current.length;
    if (available <= 0) {
      setErrorKey('max_files_error');
      return;
    }

    const accepted = files.slice(0, available);
    if (accepted.length < files.length) setErrorKey('max_files_error');

    const newItems: UploadItem[] = [];
    accepted.forEach((file) => {
      const validation = validateUploadFile(file);
      if (validation) {
        setErrorKey(validation);
        return;
      }

      const isPdf = inferContentType(file) === 'application/pdf';
      newItems.push({
        id: newId(),
        status: 'uploading',
        progress: 0,
        kind: isPdf ? 'pdf' : 'image',
        name: file.name,
        file,
        previewUrl: isPdf ? undefined : URL.createObjectURL(file),
      });
    });

    if (newItems.length === 0) return;

    setItems((current) => [...current, ...newItems]);
    await uploadInBatches(newItems);
  };

  const removeItem = (item: UploadItem) => {
    item.abort?.abort();

    if (item.value && item.previewUrl?.startsWith('blob:')) {
      removeUploadPreview(item.value);
    } else if (item.previewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(item.previewUrl);
    }

    setItems((current) => current.filter((entry) => entry.id !== item.id));
  };

  const requestRemove = (item: UploadItem) => {
    setPendingRemoval(item);
    modalRef.current?.open();
  };

  const closeRemoveModal = () => {
    modalRef.current?.close();
    setPendingRemoval(null);
  };

  const confirmRemove = () => {
    if (pendingRemoval) removeItem(pendingRemoval);
    closeRemoveModal();
  };

  const isFull = items.length >= MAX_FILES_PER_FIELD;

  return (
    <Container>
      {items.length > 0 && (
        <FilesGrid>
          {items.map((item) => (
            <Tile
              key={item.id}
              image={item.kind === 'image' ? item.previewUrl : undefined}
              data-testid={`upload-tile-${item.status}`}
            >
              {item.kind === 'pdf' && <TileBadge>.PDF</TileBadge>}
              {item.kind === 'unknown' && (
                <InsertDriveFileIcon color="disabled" fontSize="large" />
              )}
              {item.name && <TileFileName title={item.name}>{item.name}</TileFileName>}

              {item.status === 'uploading' && (
                <TileOverlay>
                  <CircularProgress
                    size={22}
                    color="inherit"
                    variant={item.progress > 0 ? 'determinate' : 'indeterminate'}
                    value={item.progress}
                  />
                  <span>{`${t('uploading')} ${item.progress}%`}</span>
                  <IconButton
                    size="small"
                    aria-label={t('cancel')}
                    onClick={() => removeItem(item)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </TileOverlay>
              )}

              {item.status === 'error' && (
                <TileOverlay tone="error">
                  <span>{t(item.errorKey ?? 'upload_failed')}</span>
                  <div>
                    <IconButton
                      size="small"
                      aria-label={t('retry')}
                      onClick={() => runUpload(item)}
                    >
                      <ReplayIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label={t('remove')}
                      onClick={() => removeItem(item)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </div>
                </TileOverlay>
              )}

              {item.status === 'done' && (
                <RemoveIcon
                  src={cancelIcon}
                  alt="remove icon"
                  onClick={() => requestRemove(item)}
                />
              )}
            </Tile>
          ))}
        </FilesGrid>
      )}

      {errorKey && <ErrorText>{t(errorKey, { max: MAX_FILES_PER_FIELD })}</ErrorText>}

      <Label htmlFor="contained-button-file" marginTop={items.length === 0 ? 70 : 0}>
        <input
          type="file"
          accept="image/*,.pdf,application/pdf"
          id="contained-button-file"
          multiple
          disabled={isFull}
          onChange={handleFilesSelected}
          data-testid="upload-input"
        />
        <MUIButton
          variant="outlined"
          component="span"
          endIcon={<FileUploadIcon />}
          disabled={isFull}
        >
          {t('send_file')}
        </MUIButton>
      </Label>
      <HelperText>
        {t('files_count', { current: items.length, max: MAX_FILES_PER_FIELD })}
      </HelperText>

      <Modal
        ref={modalRef}
        title={t('remove_file_title')}
        subtitle={t('remove_image_message')}
      >
        <Buttons>
          <Button onClick={closeRemoveModal}>{t('cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            rightIcon={<DeleteIcon />}
            onClick={confirmRemove}
          >
            {t('remove_file')}
          </Button>
        </Buttons>
      </Modal>
    </Container>
  );
}
